import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const JOB_FUNCTION_SEARCH_RESOURCE = "job-function" as const;
export const JOB_FUNCTION_SEARCH_OPERATION = "search" as const;
export const JOB_FUNCTION_SEARCH_API_PATH =
  `/v1/${JOB_FUNCTION_SEARCH_RESOURCE}/${JOB_FUNCTION_SEARCH_OPERATION}` as const;

export type JobFunctionSearchParameters = paths[typeof JOB_FUNCTION_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type JobFunctionSearchBody =
  paths[typeof JOB_FUNCTION_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type JobFunctionSearchResponse =
  paths[typeof JOB_FUNCTION_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useJobFunctionSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<JobFunctionSearchParameters, JobFunctionSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    JobFunctionSearchResponse,
    Error,
    JobFunctionSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<JobFunctionSearchParameters, JobFunctionSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      JOB_FUNCTION_SEARCH_RESOURCE,
      JOB_FUNCTION_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<JobFunctionSearchResponse>(
        JOB_FUNCTION_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
